import { recordDateValue, dateSortValue } from './labelValueSort';

// TGCLOUD-1792 — limiting the VALUES shown for one label type.
//
// A person can carry twenty test results and a dozen phenotypes. Every one of
// them printed under the node, so a well-documented patient became an unreadable
// column of text. A preset can now cap and narrow what each label prints.
//
// Deliberately modelled on labelValueSort.js, which solved the sibling problem
// (ordering the same values) — same shape, same guarantees:
//   • a per-unit capability map, so a control is never offered for data that
//     cannot answer it;
//   • sanitise on BOTH read and write, so a stored filter that outlives the code
//     that wrote it degrades to the default rather than throwing;
//   • pure and d3-free, and applied inside the shared collect helpers, so the
//     renderer, the row reservation and auto-spacing's measurement all see the
//     same set. Filtering in only one of the three is the drift that paints
//     labels at y=0 (TGCLOUD-1610).
//
// THERE IS ONE APPLICATION POINT PER UNIT, AND EVERY UNIT IN `FILTERS_FOR_UNIT`
// MUST HAVE ONE. That sounds obvious and was not: this module shipped with
// exactly one production caller — `selectRecords` in utils.js — which is reached
// only for genotypes, phenotypes and diagnoses. `tests`, `consultationDate` and
// `externalPatientId` were offered the controls, saved them, and had them badged
// as active in the modal, while the chart went on drawing every record. The
// callers now are:
//   • `selectRecords`            — genotypes, phenotypes, diseaseWithAge
//   • `selectTestRecords`        — tests            (labelText.js)
//   • `blockLines`               — consultationDate (labelText.js)
//   • `selectExternalPatientIdRows` — externalPatientId (utils.js)
// Adding a unit to `FILTERS_FOR_UNIT` without adding its application point
// re-creates a control that does nothing, which is worse than no control: the
// badge tells a clinician the chart is filtered when it is not.
//
// ── Two safety rules, and why they are not preferences ──────────────────────
//
// 1. A RECORD THAT CANNOT ANSWER A FILTER IS KEPT.
//
//    Two real populations cannot answer. Sapio-imported tests carry no date at
//    all — the importer sets dateRequested null and never sets the others — so a
//    date filter that dropped undated records would silently empty the Tests
//    block for every Sapio family. And the genotype/phenotype lists are UNIONS:
//    NCBIGene:/HP: entries promoted out of `diagnoses` carry only
//    {id, name, displayOnPedigree}, with no date and no clinicallyConfirmed.
//    `sortLabelValues` already makes the matching choice for ordering (an
//    undated record sorts last in BOTH directions, so flipping direction cannot
//    promote the records we know least about). Fail-open is the same principle:
//    absence of evidence is not evidence of absence.
//
// 2. A LIMIT THAT HIDES RECORDS MUST SAY SO.
//
//    Labels are captured in the PNG/PDF export and there is no export-exclusion
//    mechanism for them, so a filter changes what leaves the clinic while the
//    sidebar, the CanRisk export and the XML still hold everything. `hiddenCount`
//    is returned alongside the kept records precisely so the renderer can print a
//    "+ N more" row. It is not an optional nicety — a chart that silently shows
//    three of eleven tests misrepresents the patient.

export const FILTER_NONE = 'any';

// Recency windows, in years. `FILTER_NONE` means no date filter at all.
export const WITHIN_YEARS = [1, 2, 5, 10];

// Row caps offered per unit. `0` means no cap.
export const LIMIT_NONE = 0;
export const LIMIT_CHOICES = [1, 2, 3, 5, 10];

// Which controls each unit can honestly offer.
//
//   limit          — every multi-value unit; the ticket's literal ask.
//   within         — only where the unit's records carry a date recordDateValue
//                    can read. `externalPatientId` is a bare [String] with no
//                    date of any kind, which is also why labelValueSort withholds
//                    its 'date' sort key.
//   confirmedOnly  — only where the record type has the `clinicallyConfirmed`
//                    boolean (diagnosesSchema, entitySchema, genotypeSchema). A
//                    test's confirmation is not modelled, and a consultation
//                    date and an external id have no such notion.
export const FILTERS_FOR_UNIT = {
  tests: ['limit', 'within'],
  diseaseWithAge: ['limit', 'within', 'confirmedOnly'],
  phenotypes: ['limit', 'within', 'confirmedOnly'],
  genotypes: ['limit', 'within', 'confirmedOnly'],
  // Multi-row only since TGCLOUD-1781 stopped the client joining these into one
  // string. Before that there was a single opaque value and not even a date
  // filter was applicable.
  consultationDate: ['limit', 'within'],
  externalPatientId: ['limit']
};

export const DEFAULT_VALUE_FILTER = {
  limit: LIMIT_NONE,
  within: FILTER_NONE,
  confirmedOnly: false
};

// Every unit filters nothing by default. Switching the feature on therefore
// changes no chart until someone chooses otherwise — the same stance
// labelValueSort took, and the reason is stronger here: a default that hid
// clinical records would be a silent change to what every existing chart shows.
const defaultsFor = (unit) => {
  const allowed = FILTERS_FOR_UNIT[unit] || [];
  const out = {};
  if (allowed.includes('limit')) out.limit = LIMIT_NONE;
  if (allowed.includes('within')) out.within = FILTER_NONE;
  if (allowed.includes('confirmedOnly')) out.confirmedOnly = false;
  return out;
};

const asLimit = (value, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  const floored = Math.floor(n);
  // Any positive cap is honoured, not just the ones the dropdown offers: a
  // preset written by a future build with more choices must not silently lose
  // its value.
  return floored === LIMIT_NONE || floored > 0 ? floored : fallback;
};

const asWithin = (value, fallback) => {
  if (value === FILTER_NONE) return FILTER_NONE;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
};

/**
 * Read one unit's filter out of a labelsSet, falling back to that unit's
 * defaults and dropping anything the unit cannot honour.
 *
 * A preset is a stored document that outlives the code that wrote it, so an
 * unrecognised value degrades rather than throwing, and a control the unit does
 * not offer is dropped rather than half-applied.
 */
export const valueFilterFor = (labelsSet, unit) => {
  const fallback = defaultsFor(unit);
  const stored = labelsSet?.valueFilter?.[unit];
  if (!stored || typeof stored !== 'object') return fallback;
  const out = { ...fallback };
  if ('limit' in fallback) out.limit = asLimit(stored.limit, fallback.limit);
  if ('within' in fallback)
    out.within = asWithin(stored.within, fallback.within);
  if ('confirmedOnly' in fallback) out.confirmedOnly = !!stored.confirmedOnly;
  return out;
};

/**
 * Normalise a whole stored filter map: drop units that take no filter and
 * replace any unrecognised entry with that unit's default. Used when SAVING a
 * preset as well as when reading one, so a corrupt map cannot be written —
 * the same read-and-write treatment sanitiseValueSort gets, and for the same
 * reason: the seeded system presets carry `settings: {}`, so a unit can easily
 * resolve to undefined.
 */
export const sanitiseValueFilter = (stored) => {
  const out = {};
  Object.keys(FILTERS_FOR_UNIT).forEach((unit) => {
    out[unit] = valueFilterFor({ valueFilter: stored }, unit);
  });
  return out;
};

/** Whether a unit offers any filter control at all. */
export const hasValueFilter = (unit) =>
  (FILTERS_FOR_UNIT[unit] || []).length > 0;

/** How many of a unit's filters are actually doing something. */
export const activeFilterCount = (labelsSet, unit) => {
  const f = valueFilterFor(labelsSet, unit);
  let n = 0;
  if (f.limit && f.limit !== LIMIT_NONE) n += 1;
  if (f.within && f.within !== FILTER_NONE) n += 1;
  if (f.confirmedOnly) n += 1;
  return n;
};

// A consultation-date row is the date itself, already formatted for display by
// the time it reaches a label (consultationDates.js). `dateSortValue` parses a
// partial or full date string into a comparable number without ever building a
// Date, so no timezone is involved and the comparison cannot shift with the
// viewer's offset.
const recordDateFor = (unit, record) =>
  unit === 'consultationDate'
    ? dateSortValue(record)
    : recordDateValue(unit, record);

// `now` is injected so this stays pure and a test can pin a reference year
// rather than depending on the clock.
const withinCutoffYear = (years, now) => now.getFullYear() - years;

const passesWithin = (unit, record, years, now) => {
  if (!years || years === FILTER_NONE) return true;
  const key = recordDateFor(unit, record);
  // Rule 1: no usable date → kept. See the header.
  if (key === null || key === undefined) return true;
  // diseaseWithAge falls back to a bare AGE when it has no reported date
  // (recordDateValue does this so age-only diagnoses order among themselves).
  // An age is not a year and cannot be compared to a cutoff, so such a record
  // has no usable date for this purpose either.
  const year = Math.floor(key / 10000);
  if (year < 1000) return true;
  return year >= withinCutoffYear(years, now);
};

const passesConfirmed = (record, confirmedOnly) => {
  if (!confirmedOnly) return true;
  // Rule 1 again: a record that does not carry the field — every union-promoted
  // genotype/phenotype row — is kept. `false` is an assertion and is filtered;
  // absent is not.
  if (!record || !('clinicallyConfirmed' in record)) return true;
  return !!record.clinicallyConfirmed;
};

/**
 * Apply one unit's filter to an ALREADY-SORTED list of records.
 *
 * Order matters and is fixed: visibility (displayOnPedigree / the legend's
 * per-entity maps) → filter → sort → limit. The limit comes last so that
 * "sort by date, descending, show at most 1" means "the most recent", which is
 * the combination a clinician actually wants. Filtering after the limit would
 * instead mean "of the first N, whichever pass", which reads as data loss.
 *
 * @param {string} unit       orderable-unit key
 * @param {Array}  items      records, in the order they will be drawn
 * @param {object} labelsSet  the resolved preset
 * @param {Date}   [now]      reference date for the recency window
 * @returns {{items: Array, hiddenCount: number}} the rows to draw, and how many
 *   records were withheld — see rule 2 in the header. `hiddenCount` counts BOTH
 *   the filtered and the capped, because from the reader's point of view they are
 *   the same statement: this node holds more than you are being shown.
 */
export const filterLabelValues = (unit, items, labelsSet, now = new Date()) => {
  const list = Array.isArray(items) ? items : [];
  if (!hasValueFilter(unit) || !list.length) {
    return { items: [...list], hiddenCount: 0 };
  }
  const { limit, within, confirmedOnly } = valueFilterFor(labelsSet, unit);

  const passed = list.filter(
    (record) =>
      passesWithin(unit, record, within, now) &&
      passesConfirmed(record, confirmedOnly)
  );
  const capped =
    limit && limit !== LIMIT_NONE ? passed.slice(0, limit) : passed;

  return { items: capped, hiddenCount: list.length - capped.length };
};

/**
 * The overflow row's text, or '' when nothing is hidden.
 *
 * One phrasing, in one place, so the chart, the reservation and auto-spacing's
 * width measurement all agree on the exact string — a row whose width is
 * measured from different text than it draws is the TGCLOUD-1610 class of bug.
 */
export const overflowRowText = (hiddenCount) =>
  hiddenCount > 0 ? `+ ${hiddenCount} more` : '';
